import React, { useCallback, useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import Pagination from "../../components/admin/Pagination";
import { fetchCategories, fetchDeviceById } from "../../services/deviceApi";
import {
  fetchDefectsByDevice,
  toggleDeviceCatalogDefect,
  updateDeviceDefectImpact,
} from "../../services/defectApi";
import "../../styles/admin-common.css";

const PAGE_SIZE = 20;

const ModelDefectOverrides = () => {
  const { deviceId } = useParams();
  const navigate = useNavigate();
  const [device, setDevice] = useState(null);
  const [categories, setCategories] = useState([]);
  const [defects, setDefects] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");
  const [currentPage, setCurrentPage] = useState(0);
  const [editingId, setEditingId] = useState(null);
  const [impactDraft, setImpactDraft] = useState("");

  const productTypeName = categories.find((c) => String(c.id) === String(device?.categoryId))?.name || "—";

  const load = useCallback(async () => {
    if (!deviceId) return;
    setLoading(true);
    setError("");
    try {
      const [dev, cats, list] = await Promise.all([
        fetchDeviceById(deviceId),
        fetchCategories(),
        fetchDefectsByDevice(deviceId),
      ]);
      setDevice(dev);
      setCategories(cats);
      setDefects(list);
    } catch (err) {
      setError(err.message);
      setDefects([]);
    } finally {
      setLoading(false);
    }
  }, [deviceId]);

  useEffect(() => {
    load();
  }, [load]);

  const totalPages = Math.max(1, Math.ceil(defects.length / PAGE_SIZE));
  const pageDefects = defects.slice(currentPage * PAGE_SIZE, currentPage * PAGE_SIZE + PAGE_SIZE);

  const startEdit = (d) => {
    setEditingId(d.id);
    setImpactDraft(String(d.priceImpact ?? d.defaultPriceImpact ?? ""));
    setSuccess("");
  };

  const saveImpact = async (defect) => {
    setError("");
    setSuccess("");
    try {
      const value = impactDraft === "" ? null : parseFloat(impactDraft);
      await updateDeviceDefectImpact(deviceId, defect.id, value);
      setEditingId(null);
      setSuccess("Impact updated.");
      await load();
    } catch (err) {
      setError(err.message);
    }
  };

  const resetImpact = async (defect) => {
    setError("");
    setSuccess("");
    try {
      await updateDeviceDefectImpact(deviceId, defect.id, null);
      setEditingId(null);
      setSuccess("Reset to type default.");
      await load();
    } catch (err) {
      setError(err.message);
    }
  };

  const handleToggle = async (defect) => {
    setError("");
    setSuccess("");
    try {
      await toggleDeviceCatalogDefect(deviceId, defect.id);
      await load();
    } catch (err) {
      setError(err.message);
    }
  };

  if (!deviceId) {
    return (
      <div className="admin-page">
        <p className="error-msg">Missing model id.</p>
        <Link to="/models">← Back to models</Link>
      </div>
    );
  }

  return (
    <div className="admin-page">
      <p className="breadcrumb">
        <Link to="/models">Models</Link>
        <span> / </span>
        <span>{device ? `${device.brandName} ${device.modelName}` : "…"}</span>
        <span> / Defects</span>
      </p>

      <h2>Model defect overrides</h2>
      <p className="subtitle">
        {device ? (
          <>
            <strong>{device.brandName} {device.modelName}</strong> inherits defects from the{" "}
            <strong>{productTypeName}</strong> central catalog. Adjust impact % or hide a defect for this model only.
            To add or edit defect definitions, use{" "}
            <Link to={`/defects?categoryId=${device.categoryId || ""}`}>Central defects</Link>.
            New models can inherit via product type or use <Link to="/models">Clone</Link> on the models list.
          </>
        ) : (
          "Loading model…"
        )}
      </p>

      {error && <p className="error-msg">{error}</p>}
      {success && <p className="success-msg">{success}</p>}

      <div className="admin-filters">
        <button type="button" className="admin-btn secondary" onClick={() => navigate("/models")}>
          ← Back to models
        </button>
      </div>

      {loading ? (
        <p>Loading…</p>
      ) : defects.length === 0 ? (
        <p className="empty-msg">
          No central defects for {productTypeName}.{" "}
          <Link to={`/defects?categoryId=${device?.categoryId || ""}`}>Add defects to the product type catalog</Link>.
        </p>
      ) : (
        <>
          <p className="subtitle">{defects.length} inherited defect{defects.length === 1 ? "" : "s"}</p>
          <table className="admin-table">
            <thead>
              <tr>
                <th>Title</th>
                <th>Category</th>
                <th>Type default %</th>
                <th>Model impact %</th>
                <th>On this model</th>
                <th></th>
              </tr>
            </thead>
            <tbody>
              {pageDefects.map((d) => {
                const hasOverride = d.priceImpactOverride != null;
                const isEditing = editingId === d.id;
                return (
                  <tr key={d.id}>
                    <td>{d.title}</td>
                    <td>{d.category}</td>
                    <td>{d.defaultPriceImpact ?? "—"}%</td>
                    <td>
                      {isEditing ? (
                        <input
                          type="number"
                          className="inline-input"
                          value={impactDraft}
                          onChange={(e) => setImpactDraft(e.target.value)}
                          style={{ width: 72 }}
                        />
                      ) : (
                        <>
                          {d.priceImpact}%
                          {hasOverride && <small className="field-hint"> (override)</small>}
                        </>
                      )}
                    </td>
                    <td>{d.isActive ? "Active" : "Hidden"}</td>
                    <td className="actions-cell">
                      {isEditing ? (
                        <>
                          <button type="button" className="admin-btn" onClick={() => saveImpact(d)}>Save</button>
                          <button type="button" className="admin-btn secondary" onClick={() => setEditingId(null)}>Cancel</button>
                        </>
                      ) : (
                        <>
                          <button type="button" className="admin-btn secondary" onClick={() => startEdit(d)}>Edit %</button>
                          {hasOverride && (
                            <button type="button" className="admin-btn secondary" onClick={() => resetImpact(d)}>Reset</button>
                          )}
                          <button type="button" className="admin-btn" onClick={() => handleToggle(d)}>
                            {d.isActive ? "Hide" : "Show"}
                          </button>
                        </>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
          <Pagination page={currentPage} totalPages={totalPages} onPageChange={setCurrentPage} />
        </>
      )}
    </div>
  );
};

export default ModelDefectOverrides;
